import { useTranslate } from '@tolgee/react';
import { Button } from 'react-bootstrap';
import React, { useState } from 'react';
import { Form, Row, Col } from 'react-bootstrap';

export const AddImageButton = ({ handleClick, disabled }) => {
  const { t } = useTranslate();
  return (
    <Button
      onClick={handleClick}
      variant="secondary"
      size="sm"
      disabled={disabled}
    >
      <div className="d-flex align-items-center justify-content-center gap-1">
        <i className="fa-duotone fa-plus"></i>
        {t('lomavisCreator.addImage')}
      </div>
    </Button>
  );
};

export const UseTemplateButton = ({ handleClick, disabled }) => {
  const { t } = useTranslate();
  return (
    <Button
      onClick={handleClick}
      variant="primary"
      size="sm"
      disabled={disabled}
    >
      <div className="d-flex align-items-center justify-content-center gap-1">
        <i className="fa-duotone fa-layer-group"></i>
        {t('lomavisCreator.multiEditor.useTemplate', 'Use overlay/template')}
      </div>
    </Button>
  );
};

export const EditorHeader = ({ children }) => {
  return (
    <div
      style={{}}
      className="h-100 w-100 h-60px d-flex  justify-content-between bg-white p-3 rounded-3"
    >
      {children}
    </div>
  );
};

interface TimeSelectProps {
  onTimeChange: (hour: number, minute: number) => void;
  hour: number;
  minute: number;
  setHour: React.Dispatch<React.SetStateAction<number>>;
  setMinute: React.Dispatch<React.SetStateAction<number>>;
}

interface DaySelectProps {
  onTimeChange: (hour: number, minute: number, day: number) => void;
  hour: number;
  minute: number;
  day: number;
  setDay: React.Dispatch<React.SetStateAction<number>>;
  setHour: React.Dispatch<React.SetStateAction<number>>;
  setMinute: React.Dispatch<React.SetStateAction<number>>;
  t: any;
}

export const TimeSelect: React.FC<TimeSelectProps> = ({
  onTimeChange,
  hour,
  setHour,
  minute,
  setMinute
}) => {
  const handleHourChange = (e) => {
    const newHour = parseInt(e.target.value, 10);
    setHour(newHour);
    onTimeChange(newHour, minute);
  };

  const handleMinuteChange = (e) => {
    const newMinute = parseInt(e.target.value, 10);
    setMinute(newMinute);
    onTimeChange(hour, newMinute);
  };

  const generateOptionsMinutes = (range: number) => {
    return Array.from(Array(range).keys()).map((value) => (
      <option key={value} value={value}>
        {value.toString().padStart(2, '0')}
      </option>
    ));
  };
  const generateOptionsHours = (range: number) => {
    return Array.from(Array(range).keys()).map((value) => (
      <option key={value} value={value}>
        {value.toString().padStart(2)}
      </option>
    ));
  };

  return (
    <Form className="w-100">
      <Row className="d-flex align-items-center justify-content-start g-2">
        <Col>
          <Form.Group controlId="hourSelect">
            <Form.Select
              as="select"
              value={hour}
              onChange={(e) => handleHourChange(e)}
            >
              {generateOptionsHours(24)}
            </Form.Select>
          </Form.Group>
        </Col>
        :
        <Col>
          <Form.Group controlId="minuteSelect">
            <Form.Select
              as="select"
              value={minute}
              onChange={(e) => handleMinuteChange(e)}
            >
              {generateOptionsMinutes(60)}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
};

export const DayHourMinuteSelect: React.FC<DaySelectProps> = ({
  onTimeChange,
  day,
  setDay,
  hour,
  setHour,
  minute,
  setMinute,
  t
}) => {
  const handleDayChange = (e) => {
    const newDay = e.target.value === '' ? null : parseInt(e.target.value, 10);
    setDay(newDay);
    onTimeChange(newDay, hour, minute);
  };

  const handleHourChange = (e) => {
    const newHour = e.target.value === '' ? null : parseInt(e.target.value, 10);
    setHour(newHour);
    onTimeChange(day, newHour, minute);
  };

  const handleMinuteChange = (e) => {
    const newMinute =
      e.target.value === '' ? null : parseInt(e.target.value, 10);
    setMinute(newMinute);
    onTimeChange(day, hour, newMinute);
  };

  const generateOptionsDays = (range: number) => {
    return Array.from(Array(range).keys()).map((value) => (
      <option key={value} value={value}>
        {value}
      </option>
    ));
  };

  const generateOptionsMinutes = (range: number) => {
    return Array.from(Array(range).keys()).map((value) => (
      <option key={value} value={value}>
        {value.toString().padStart(2, '0')}
      </option>
    ));
  };

  const generateOptionsHours = (range: number) => {
    return Array.from(Array(range).keys()).map((value) => (
      <option key={value} value={value}>
        {value.toString().padStart(2)}
      </option>
    ));
  };

  return (
    <Form className="w-100">
      <Row className="d-flex align-items-center justify-content-start g-2">
        <Col>
          <Form.Group controlId="daySelect">
            <Form.Label>{t('lomavisCreator.publishingDelay.day')}</Form.Label>
            <Form.Select
              as="select"
              value={day ?? ''}
              onChange={(e) => handleDayChange(e)}
            >
              <option value=""></option> {/* Default empty option */}
              {generateOptionsDays(30)} {/* You can set any range for days */}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col>
          <Form.Label>{t('lomavisCreator.publishingDelay.hour')}</Form.Label>
          <Form.Group controlId="hourSelect">
            <Form.Select
              as="select"
              value={hour ?? ''}
              onChange={(e) => handleHourChange(e)}
            >
              <option value=""></option> {/* Default empty option */}
              {generateOptionsHours(24)}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col>
          <Form.Label>{t('lomavisCreator.publishingDelay.minute')}</Form.Label>
          <Form.Group controlId="minuteSelect">
            <Form.Select
              as="select"
              value={minute ?? ''}
              onChange={(e) => handleMinuteChange(e)}
            >
              <option value=""></option> {/* Default empty option */}
              {generateOptionsMinutes(60)}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
};
